import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatTimeAgo } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  confirm,
} from "@devhop/ui";
import { Link, useNavigate, useParams, useRouter } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  EyeOffIcon,
  PackageIcon,
  PencilIcon,
  RotateCcwIcon,
  StoreIcon,
  TrashIcon,
} from "lucide-react";
import type {
  ListingProductDetail,
  ListingProductShopRow,
  ListingProductSkuGroup,
} from "../api/client";
import {
  useBulkUpdateListingsMutation,
  useListingProductQuery,
} from "../api/queries";

const MODERATION_LIST_SEARCH = { limit: 20, offset: 0 } as const;

const STATUS_LABELS: Record<
  string,
  { label: string; variant: "success" | "destructive" | "secondary" }
> = {
  active: { label: "ໃຊ້ງານ", variant: "success" },
  hidden: { label: "ເຊື່ອງ", variant: "secondary" },
  removed: { label: "ລຶບແລ້ວ", variant: "destructive" },
};

const CONDITION_LABELS: Record<string, string> = {
  new: "ໃໝ່",
  used: "ໃຊ້ແລ້ວ",
  refurb: "ປັບປຸງແລ້ວ",
};

function skuStatusCounts(shops: ListingProductShopRow[]) {
  return shops.reduce(
    (acc, s) => {
      if (s.status === "active") acc.active += 1;
      else if (s.status === "hidden") acc.hidden += 1;
      else if (s.status === "removed") acc.removed += 1;
      return acc;
    },
    { active: 0, hidden: 0, removed: 0 },
  );
}

function ShopListingCard({ shop }: { shop: ListingProductShopRow }) {
  const bulkUpdate = useBulkUpdateListingsMutation();
  const nav = useNavigate();
  const isPending = bulkUpdate.isPending;
  const status = STATUS_LABELS[shop.status] ?? {
    label: shop.status,
    variant: "secondary" as const,
  };

  const runAction = async (
    nextStatus: "hidden" | "removed" | "active",
    title: string,
    description: string,
    actionText: string,
    destructive = false,
  ) => {
    const ok = await confirm({
      title,
      description,
      actionText,
      ActionProps: destructive ? { variant: "destructive" } : undefined,
    });
    if (!ok) return;
    await bulkUpdate.mutateAsync({ ids: [shop.listingId], status: nextStatus });
  };

  const hasCustomPrice =
    shop.displayPrice != null &&
    shop.displayCurrency != null &&
    (shop.displayPrice !== shop.price ||
      shop.displayCurrency !== shop.currency);

  return (
    <div
      className={cn(
        "group rounded-xl border bg-card/80 p-3.5 transition-all hover:border-primary/25 hover:shadow-sm sm:p-4",
        shop.status === "removed" && "opacity-70",
        shop.status === "active" &&
        "border-emerald-500/20 bg-emerald-500/2",
        shop.status === "hidden" && "border-dashed bg-muted/20",
      )}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div
            className={cn(
              "flex size-11 shrink-0 items-center justify-center rounded-xl border",
              shop.status === "active"
                ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : "border-border bg-muted/50 text-muted-foreground",
            )}
          >
            <StoreIcon className="size-5" aria-hidden />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/app/shops/$id"
                params={{ id: shop.shopId }}
                className="truncate font-semibold text-sm hover:text-primary hover:underline"
              >
                {shop.shopName}
              </Link>
              <Badge variant={status.variant} className="shrink-0 text-[10px]">
                {status.label}
              </Badge>
            </div>

            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-xs">
              <span className="inline-flex items-center gap-1 font-mono">
                SKU {shop.shopSku}
              </span>
              <span className="inline-flex items-center gap-1">
                <ClockIcon className="size-3" aria-hidden />
                {formatTimeAgo(shop.updatedAt)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 lg:shrink-0">
          <div className="sm:text-right">
            <p className="font-bold text-base text-primary tabular-nums">
              {formatPrice(shop.price, shop.currency)}
            </p>
            {hasCustomPrice ? (
              <p className="text-muted-foreground text-[11px]">
                ສະແດງ:{" "}
                {formatPrice(shop.displayPrice!, shop.displayCurrency!)}
              </p>
            ) : (
              <p className="text-muted-foreground text-[11px]">ລາຄາ marketplace</p>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 gap-1"
              disabled={isPending}
              onClick={() =>
                nav({
                  to: "/app/moderation/listings/$id",
                  params: { id: shop.listingId },
                })
              }
            >
              <PencilIcon className="size-3.5" aria-hidden />
              <span className="hidden sm:inline">ແກ້ລາຄາ</span>
            </Button>

            {shop.status === "active" ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-8 gap-1"
                disabled={isPending}
                onClick={() =>
                  void runAction(
                    "hidden",
                    "ເຊື່ອງລາຍການ",
                    `ເຊື່ອງ listing ຂອງ ${shop.shopName}?`,
                    "ເຊື່ອງ",
                  )
                }
              >
                <EyeOffIcon className="size-3.5" aria-hidden />
                <span className="hidden sm:inline">ເຊື່ອງ</span>
              </Button>
            ) : null}

            {shop.status !== "active" && shop.status !== "removed" ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-8 gap-1"
                disabled={isPending}
                onClick={() =>
                  void runAction(
                    "active",
                    "ເປີດຄືນ",
                    `ເປີດຄືນ listing ຂອງ ${shop.shopName}?`,
                    "ເປີດຄືນ",
                  )
                }
              >
                <RotateCcwIcon className="size-3.5" aria-hidden />
                <span className="hidden sm:inline">ເປີດຄືນ</span>
              </Button>
            ) : null}

            {shop.status !== "removed" ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 gap-1 text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={isPending}
                onClick={() =>
                  void runAction(
                    "removed",
                    "ລຶບ listing",
                    `ລຶບ listing ຂອງ ${shop.shopName}?`,
                    "ລຶບ",
                    true,
                  )
                }
              >
                <TrashIcon className="size-3.5" aria-hidden />
                <span className="hidden sm:inline">ລຶບ</span>
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function SkuShopList({ sku }: { sku: ListingProductSkuGroup }) {
  const oem = sku.oemPartNo?.trim() || sku.catalogSkuId.slice(0, 8);
  const cond = sku.condition
    ? (CONDITION_LABELS[sku.condition] ?? sku.condition)
    : null;
  const skuImage = sku.imageUrl ? resolveImageSrc(sku.imageUrl) : null;
  const counts = skuStatusCounts(sku.shops);

  return (
    <Card className="overflow-hidden py-0 gap-0 shadow-sm">
      <CardHeader className="border-b bg-muted/20 px-4 py-4 sm:px-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-background shadow-sm">
              {skuImage ? (
                <img
                  src={skuImage}
                  alt=""
                  className="size-full object-cover"
                />
              ) : (
                <PackageIcon
                  className="size-6 text-muted-foreground"
                  aria-hidden
                />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="font-mono text-base tracking-tight">
                  {oem}
                </CardTitle>
                {cond ? (
                  <Badge variant="outline" className="font-sans text-xs">
                    {cond}
                  </Badge>
                ) : null}
              </div>
              <p className="mt-1 text-muted-foreground text-xs">
                SKU/OEM · {sku.shops.length} ຮ້ານ
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {counts.active > 0 ? (
              <Badge variant="success" className="text-[10px]">
                {counts.active} ໃຊ້ງານ
              </Badge>
            ) : null}
            {counts.hidden > 0 ? (
              <Badge variant="secondary" className="text-[10px]">
                {counts.hidden} ເຊື່ອງ
              </Badge>
            ) : null}
            {counts.removed > 0 ? (
              <Badge variant="destructive" className="text-[10px]">
                {counts.removed} ລຶບ
              </Badge>
            ) : null}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-2.5 p-3 sm:p-4">
        {sku.shops.map((shop) => (
          <ShopListingCard key={shop.listingId} shop={shop} />
        ))}
      </CardContent>
    </Card>
  );
}

function ListingProductDetailContent({
  product,
}: {
  product: ListingProductDetail;
}) {
  const router = useRouter();
  const imageSrc = product.imageUrl ? resolveImageSrc(product.imageUrl) : null;

  return (
    <>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-muted shadow-sm">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={product.productName}
                className="size-full object-cover"
              />
            ) : (
              <PackageIcon className="size-7 text-muted-foreground" aria-hidden />
            )}
          </div>
          <div className="min-w-0">
            <h1 className="font-bold text-2xl tracking-tight">
              {product.productName}
            </h1>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="outline">{product.skuCount} SKU</Badge>
              <Badge variant="outline">{product.shopCount} ຮ້ານ</Badge>
              <Badge variant="outline">{product.listingCount} listing</Badge>
              {product.categoryName ? (
                <Badge variant="secondary">{product.categoryName}</Badge>
              ) : null}
            </div>
            {product.productDescription ? (
              <p className="mt-3 max-w-2xl text-muted-foreground text-sm leading-relaxed">
                {product.productDescription}
              </p>
            ) : null}
          </div>
        </div>
        <Button variant="outline" onClick={() => router.history.back()}>
          <ArrowLeftIcon className="size-4" />
          ກັບຄືນ
        </Button>
      </div>

      {product.skus.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            ບໍ່ມີ listing ສຳລັບສິນຄ້ານີ້
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <ChevronRightIcon className="size-4" aria-hidden />
            <span>
              ເບິ່ງ SKU/OEM ແລະຮ້ານທີ່ຂາຍຢູ່ດ້ານລຸ່ມ
            </span>
          </div>
          {product.skus.map((sku) => (
            <SkuShopList key={sku.catalogSkuId} sku={sku} />
          ))}
        </div>
      )}
    </>
  );
}

export function ListingProductDetailPage() {
  const { catalogProductId } = useParams({
    from: "/app/moderation/listings/products/$catalogProductId",
  });
  const { data: product, isLoading } = useListingProductQuery(
    catalogProductId ?? "",
  );

  if (isLoading || !product) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              {
                label: "ການກວດກາ Marketplace",
                to: "/app/moderation",
                search: MODERATION_LIST_SEARCH,
              },
              { label: "ລາຍລະອຽດ" },
            ]}
          />
        </Header>
        <Main>
          <div className="text-muted-foreground">
            {isLoading ? "ກຳລັງໂຫຼດ..." : "ບໍ່ພົບສິນຄ້າ"}
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            {
              label: "ການກວດກາ Marketplace",
              to: "/app/moderation",
              search: MODERATION_LIST_SEARCH,
            },
            { label: product.productName },
          ]}
        />
      </Header>

      <Main>
        <ListingProductDetailContent product={product} />
      </Main>
    </>
  );
}
